/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import dayjs from 'dayjs';
import {
  hasKeys,
  getFileTypeFromOutputFileS3Key,
  formatIncentivesFiles,
} from 'utils/appUtils';
import {
  FILE_GENERATION_PARAMS_STORAGE_KEY,
  GENERATE_SECTION_DESCRIPTION,
  OUTPUT_FILES,
  PUBLISH_STATUS,
} from 'utils/const';

const initialState = {
  selectedModelYear: new Date().getFullYear(),
  isSourceFilesSectionCollapsed: false,
  generateFilesSectionDescription: GENERATE_SECTION_DESCRIPTION,
  // SOURCE FILES START
  imdFile: {},
  priceMatrixFilesByModelYear: {},
  pplFilesByModelYearAndModelCode: {},
  pplFilesByModelYear: {},
  // SOURCE FILES END
  haveAllSourceFiles: false,
  // GENERATED FILES START
  isGeneratingTaxImport: false,
  taxImportFile: {},
  taxImportGenerationError: {},
  shouldOpenGetEffectiveDatesModal: false,
  shouldOpenConfirmPublishModal: false,
  pcdfStartDate: localStorage.getItem(
    FILE_GENERATION_PARAMS_STORAGE_KEY.PCDF_START_DATE,
  )
    ? dayjs(
        JSON.parse(
          localStorage.getItem(
            FILE_GENERATION_PARAMS_STORAGE_KEY.PCDF_START_DATE,
          ),
        ),
      )
    : null,
  pcdfEndDate: localStorage.getItem(
    FILE_GENERATION_PARAMS_STORAGE_KEY.PCDF_END_DATE,
  )
    ? dayjs(
        JSON.parse(
          localStorage.getItem(
            FILE_GENERATION_PARAMS_STORAGE_KEY.PCDF_END_DATE,
          ),
        ),
      )
    : null,
  pcdfDataSetId:
    JSON.parse(
      localStorage.getItem(FILE_GENERATION_PARAMS_STORAGE_KEY.PCDF_DATA_SET_ID),
    ) || '',
  userFileAction: '',
  selectedFilesToGenerateOrUpdate: {},
  selectedFilesForPublishing: {},
  pcdfFilesToBeGeneratedByModelYear: {},
  pcdfFilesByModelYearAndModelCode: {},
  pcdfsByModelYear: {},
  pcdfFileGenerationErrorsByModelYear: {},
  isGeneratingToolboxByModelYear: false,
  toolboxByModelYear: {},
  toolboxGenerationErrorsByModelYear: {},

  isGeneratingPfaByModelYear: false,
  pfaByModelYear: {},
  pfaGenerationErrorsByModelYear: {},
  // GENERATED FILES END
  // PUBLISH FILES START
  publishOverviewFiles: [],
  // PUBLISH FILES END
};

const getGenerateAndUpdateState = ({
  imdFile,
  priceMatrixFilesByModelYear,
  pplFilesByModelYearAndModelCode,
  taxImportFile,
  pcdfFilesByModelYearAndModelCode,
  toolboxByModelYear,
  pfaByModelYear,
}) => {
  const currentImdVersion = imdFile.FileVersion;
  const imdVersionUsedToGenerateTaxImport =
    taxImportFile?.EN?.Metadata?.imdversion;

  const updatedTaxImportFile = { ...taxImportFile };
  const updatedToolboxByModelYear = { ...toolboxByModelYear };
  const updatedPfaByModelYear = { ...pfaByModelYear };
  let updatedPcdfFilesByModelYearAndModelCode = {
    ...pcdfFilesByModelYearAndModelCode,
  };
  const pcdfFilesToBeGeneratedByModelYear = {};

  // tax import
  if (
    imdVersionUsedToGenerateTaxImport &&
    currentImdVersion !== imdVersionUsedToGenerateTaxImport
  ) {
    updatedTaxImportFile.EN = {
      ...updatedTaxImportFile.EN,
      shouldUpdate: true,
    };
    updatedTaxImportFile.FR = {
      ...updatedTaxImportFile.FR,
      shouldUpdate: true,
    };
  }

  // toolbox
  Object.keys(toolboxByModelYear).forEach((modelYear) => {
    const imdVersionUsedToGenerateToolbox =
      toolboxByModelYear[modelYear]?.Metadata?.imdversion;

    if (
      imdVersionUsedToGenerateToolbox &&
      currentImdVersion !== imdVersionUsedToGenerateToolbox
    ) {
      updatedToolboxByModelYear[modelYear] = {
        ...updatedToolboxByModelYear[modelYear],
        shouldUpdate: true,
      };
    }

    const pplFilesByModelCode = pplFilesByModelYearAndModelCode[modelYear];
    Object.keys(pplFilesByModelCode).forEach((modelCode) => {
      const currentPplVersion = pplFilesByModelCode[modelCode].FileVersion;
      const pplVersionUsedToGenerateToolbox =
        toolboxByModelYear[modelYear]?.Metadata?.[modelCode.toLowerCase()];
      if (currentPplVersion !== pplVersionUsedToGenerateToolbox) {
        updatedToolboxByModelYear[modelYear] = {
          ...updatedToolboxByModelYear[modelYear],
          shouldUpdate: true,
        };
      }
    });
  });

  // pfa
  Object.keys(pfaByModelYear).forEach((modelYear) => {
    const imdVersionUsedToGeneratePfa =
      pfaByModelYear[modelYear]?.Metadata?.imdversion;

    if (
      imdVersionUsedToGeneratePfa &&
      currentImdVersion !== imdVersionUsedToGeneratePfa
    ) {
      updatedPfaByModelYear[modelYear] = {
        ...updatedPfaByModelYear[modelYear],
        shouldUpdate: true,
      };
    }

    const pplFilesByModelCode = pplFilesByModelYearAndModelCode[modelYear];
    Object.keys(pplFilesByModelCode).forEach((modelCode) => {
      const currentPplVersion = pplFilesByModelCode[modelCode].FileVersion;
      const pplVersionUsedToGeneratePfa =
        pfaByModelYear[modelYear]?.Metadata?.[modelCode.toLowerCase()];
      if (currentPplVersion !== pplVersionUsedToGeneratePfa) {
        updatedPfaByModelYear[modelYear] = {
          ...updatedPfaByModelYear[modelYear],
          shouldUpdate: true,
        };
      }
    });
  });

  // if any of the PPL file was updated, then the related PCDF file can be updated
  Object.keys(pplFilesByModelYearAndModelCode).forEach((modelYear) => {
    Object.keys(pplFilesByModelYearAndModelCode[modelYear]).forEach(
      (modelCode) => {
        const pplFile = pplFilesByModelYearAndModelCode[modelYear][modelCode];
        const pcdfFile =
          pcdfFilesByModelYearAndModelCode?.[modelYear]?.[modelCode];

        // has related PCDF
        if (hasKeys(pcdfFilesByModelYearAndModelCode) && pcdfFile) {
          const currentPplVersion = pplFile.FileVersion;
          const imdVersionUsedToGeneratePcdf =
            pcdfFile?.EN?.Metadata?.imdversion;
          const currentPriceMatrixVersion =
            priceMatrixFilesByModelYear[modelYear]?.FileVersion;
          const priceMatrixVersionUsedToGeneratePcdf =
            pcdfFile?.EN?.Metadata?.pricematrixversion;
          const pplVersionUsedToGeneratePcdf =
            pcdfFile?.EN?.Metadata?.pplversion;

          // if source versions doesn't match the ones that were used to generate the file
          if (
            currentPplVersion !== pplVersionUsedToGeneratePcdf ||
            currentImdVersion !== imdVersionUsedToGeneratePcdf ||
            currentPriceMatrixVersion !== priceMatrixVersionUsedToGeneratePcdf
          ) {
            updatedPcdfFilesByModelYearAndModelCode = {
              ...updatedPcdfFilesByModelYearAndModelCode,
              [modelYear]: {
                ...updatedPcdfFilesByModelYearAndModelCode[modelYear],
                [modelCode]: {
                  ...updatedPcdfFilesByModelYearAndModelCode[modelYear][
                    modelCode
                  ],
                  EN: {
                    ...updatedPcdfFilesByModelYearAndModelCode[modelYear][
                      modelCode
                    ].EN,
                    shouldUpdate: true,
                  },
                  FR: {
                    ...updatedPcdfFilesByModelYearAndModelCode[modelYear][
                      modelCode
                    ].FR,
                    shouldUpdate: true,
                  },
                },
              },
            };
          }
        } else {
          if (!pcdfFilesToBeGeneratedByModelYear[modelYear]) {
            pcdfFilesToBeGeneratedByModelYear[modelYear] = [];
          }
          pcdfFilesToBeGeneratedByModelYear[modelYear].push(pplFile);
        }
      },
    );
  });

  return {
    updatedTaxImportFile,
    updatedPcdfFilesByModelYearAndModelCode,
    pcdfFilesToBeGeneratedByModelYear,
    updatedToolboxByModelYear,
    updatedPfaByModelYear,
  };
};

const checkAndGetGenerateAndUpdateStates = ({
  imdFile,
  priceMatrixFilesByModelYear,
  pplFilesByModelYearAndModelCode,
  taxImportFile,
  pcdfFilesByModelYearAndModelCode,
  toolboxByModelYear,
  pfaByModelYear,
  publishOverviewFiles,
}) => {
  let generateFilesSectionDescription = GENERATE_SECTION_DESCRIPTION;
  let updateAndGenerateStates = {};

  const haveAllSourceFiles = Boolean(
    hasKeys(imdFile) &&
      hasKeys(priceMatrixFilesByModelYear) &&
      hasKeys(pplFilesByModelYearAndModelCode),
  );

  if (haveAllSourceFiles) {
    generateFilesSectionDescription = `Click below to generate files. ${imdFile.DisplayName}, Price Matrix and PPL files will be used to generate output files.`;
    updateAndGenerateStates = getGenerateAndUpdateState({
      imdFile,
      priceMatrixFilesByModelYear,
      pplFilesByModelYearAndModelCode,
      taxImportFile,
      pcdfFilesByModelYearAndModelCode,
      toolboxByModelYear,
      pfaByModelYear,
    });
  }

  const {
    updatedTaxImportFile,
    updatedPcdfFilesByModelYearAndModelCode,
    pcdfFilesToBeGeneratedByModelYear,
    updatedToolboxByModelYear,
    updatedPfaByModelYear,
  } = updateAndGenerateStates || {};

  const out = {
    haveAllSourceFiles,
    generateFilesSectionDescription,
    formattedPplFilesByModelYear: {},
    formattedTaxImportFile: {},
    updatedPcdfFilesByModelYearAndModelCode:
      updatedPcdfFilesByModelYearAndModelCode ||
      pcdfFilesByModelYearAndModelCode,
    formattedPcdfsByModelYear: {},
    pcdfFilesToBeGeneratedByModelYear: pcdfFilesToBeGeneratedByModelYear || {},
    updatedToolboxByModelYear: updatedToolboxByModelYear || toolboxByModelYear,
    updatedPfaByModelYear: updatedPfaByModelYear || pfaByModelYear,
    updatedPublishOverviewFiles: [],
  };

  const {
    pplFilesByModelYear,
    taxImport,
    pcdfsByModelYear,
    updatedPublishOverviewFiles,
  } = formatIncentivesFiles({
    pplFilesByModelYearAndModelCode,
    taxImportFile: updatedTaxImportFile || {},
    pcdfFilesByModelYearAndModelCode:
      out.updatedPcdfFilesByModelYearAndModelCode,
    publishOverviewFiles,
  });

  out.formattedPplFilesByModelYear = pplFilesByModelYear;
  out.formattedTaxImportFile = taxImport;
  out.formattedPcdfsByModelYear = pcdfsByModelYear;
  out.updatedPublishOverviewFiles = updatedPublishOverviewFiles || [];

  return out;
};

const incentivesSlice = createSlice({
  name: 'incentives',
  initialState,
  reducers: {
    setSelectedModelYear: (state, action) => {
      state.selectedModelYear = action.payload;
    },
    setIsSourceFilesSectionCollapsed: (state, action) => {
      state.isSourceFilesSectionCollapsed = action.payload;
    },
    toggleCollapseSourceFilesSection: (state) => {
      state.isSourceFilesSectionCollapsed =
        !state.isSourceFilesSectionCollapsed;
    },
    setIMDFile: (state, action) => {
      state.imdFile = action.payload;
      const {
        haveAllSourceFiles,
        generateFilesSectionDescription,
        formattedPplFilesByModelYear,
        formattedTaxImportFile,
        updatedPcdfFilesByModelYearAndModelCode,
        formattedPcdfsByModelYear,
        pcdfFilesToBeGeneratedByModelYear,
        updatedToolboxByModelYear,
        updatedPfaByModelYear,
        updatedPublishOverviewFiles,
      } = checkAndGetGenerateAndUpdateStates({
        imdFile: action.payload,
        priceMatrixFilesByModelYear: state.priceMatrixFilesByModelYear,
        pplFilesByModelYearAndModelCode: state.pplFilesByModelYearAndModelCode,
        taxImportFile: state.taxImportFile,
        pcdfFilesByModelYearAndModelCode:
          state.pcdfFilesByModelYearAndModelCode,
        toolboxByModelYear: state.toolboxByModelYear,
        pfaByModelYear: state.pfaByModelYear,
        publishOverviewFiles: state.publishOverviewFiles,
      });
      state.haveAllSourceFiles = haveAllSourceFiles;
      state.generateFilesSectionDescription = generateFilesSectionDescription;
      state.pplFilesByModelYear = formattedPplFilesByModelYear;
      state.taxImportFile = formattedTaxImportFile;
      state.pcdfFilesToBeGeneratedByModelYear =
        pcdfFilesToBeGeneratedByModelYear;
      state.toolboxByModelYear = updatedToolboxByModelYear;
      state.pcdfFilesByModelYearAndModelCode =
        updatedPcdfFilesByModelYearAndModelCode;
      state.pcdfsByModelYear = formattedPcdfsByModelYear;
      state.pfaByModelYear = updatedPfaByModelYear;
      state.publishOverviewFiles = updatedPublishOverviewFiles;
    },
    setPriceMatrixFiles: (state, action) => {
      const latestPriceMatrixFilesByModelYear = action.payload;
      state.priceMatrixFilesByModelYear = latestPriceMatrixFilesByModelYear;
      const {
        haveAllSourceFiles,
        generateFilesSectionDescription,
        formattedPplFilesByModelYear,
        formattedTaxImportFile,
        updatedPcdfFilesByModelYearAndModelCode,
        formattedPcdfsByModelYear,
        pcdfFilesToBeGeneratedByModelYear,
        updatedToolboxByModelYear,
        updatedPfaByModelYear,
        updatedPublishOverviewFiles,
      } = checkAndGetGenerateAndUpdateStates({
        imdFile: state.imdFile,
        priceMatrixFilesByModelYear: latestPriceMatrixFilesByModelYear,
        pplFilesByModelYearAndModelCode: state.pplFilesByModelYearAndModelCode,
        taxImportFile: state.taxImportFile,
        pcdfFilesByModelYearAndModelCode:
          state.pcdfFilesByModelYearAndModelCode,
        toolboxByModelYear: state.toolboxByModelYear,
        pfaByModelYear: state.pfaByModelYear,
        publishOverviewFiles: state.publishOverviewFiles,
      });
      state.pcdfFilesByModelYearAndModelCode =
        updatedPcdfFilesByModelYearAndModelCode;
      state.haveAllSourceFiles = haveAllSourceFiles;
      state.generateFilesSectionDescription = generateFilesSectionDescription;
      state.pplFilesByModelYear = formattedPplFilesByModelYear;
      state.taxImportFile = formattedTaxImportFile;
      state.pcdfFilesToBeGeneratedByModelYear =
        pcdfFilesToBeGeneratedByModelYear;
      state.pcdfsByModelYear = formattedPcdfsByModelYear;
      state.toolboxByModelYear = updatedToolboxByModelYear;
      state.pfaByModelYear = updatedPfaByModelYear;
      state.publishOverviewFiles = updatedPublishOverviewFiles;
    },
    setPPLFiles: (state, action) => {
      const latestPplFilesByModelYearAndModelCode = action.payload;
      state.pplFilesByModelYearAndModelCode =
        latestPplFilesByModelYearAndModelCode;
      const {
        haveAllSourceFiles,
        generateFilesSectionDescription,
        formattedPplFilesByModelYear,
        formattedTaxImportFile,
        updatedPcdfFilesByModelYearAndModelCode,
        formattedPcdfsByModelYear,
        pcdfFilesToBeGeneratedByModelYear,
        updatedToolboxByModelYear,
        updatedPfaByModelYear,
        updatedPublishOverviewFiles,
      } = checkAndGetGenerateAndUpdateStates({
        imdFile: state.imdFile,
        priceMatrixFilesByModelYear: state.priceMatrixFilesByModelYear,
        pplFilesByModelYearAndModelCode: latestPplFilesByModelYearAndModelCode,
        taxImportFile: state.taxImportFile,
        pcdfFilesByModelYearAndModelCode:
          state.pcdfFilesByModelYearAndModelCode,
        toolboxByModelYear: state.toolboxByModelYear,
        pfaByModelYear: state.pfaByModelYear,
        publishOverviewFiles: state.publishOverviewFiles,
      });
      state.pcdfFilesByModelYearAndModelCode =
        updatedPcdfFilesByModelYearAndModelCode;
      state.haveAllSourceFiles = haveAllSourceFiles;
      state.generateFilesSectionDescription = generateFilesSectionDescription;
      state.pplFilesByModelYear = formattedPplFilesByModelYear;
      state.taxImportFile = formattedTaxImportFile;
      state.pcdfFilesToBeGeneratedByModelYear =
        pcdfFilesToBeGeneratedByModelYear;
      state.pcdfsByModelYear = formattedPcdfsByModelYear;
      state.toolboxByModelYear = updatedToolboxByModelYear;
      state.pfaByModelYear = updatedPfaByModelYear;
      state.publishOverviewFiles = updatedPublishOverviewFiles;
    },
    setTaxImportFile: (state, action) => {
      const {
        haveAllSourceFiles,
        generateFilesSectionDescription,
        formattedPplFilesByModelYear,
        formattedTaxImportFile,
        updatedPcdfFilesByModelYearAndModelCode,
        formattedPcdfsByModelYear,
        pcdfFilesToBeGeneratedByModelYear,
        updatedToolboxByModelYear,
        updatedPfaByModelYear,
        updatedPublishOverviewFiles,
      } = checkAndGetGenerateAndUpdateStates({
        imdFile: state.imdFile,
        priceMatrixFilesByModelYear: state.priceMatrixFilesByModelYear,
        pplFilesByModelYearAndModelCode: state.pplFilesByModelYearAndModelCode,
        taxImportFile: action.payload,
        pcdfFilesByModelYearAndModelCode:
          state.pcdfFilesByModelYearAndModelCode,
        toolboxByModelYear: state.toolboxByModelYear,
        pfaByModelYear: state.pfaByModelYear,
        publishOverviewFiles: state.publishOverviewFiles,
      });
      state.haveAllSourceFiles = haveAllSourceFiles;
      state.generateFilesSectionDescription = generateFilesSectionDescription;
      state.pplFilesByModelYear = formattedPplFilesByModelYear;
      state.pcdfFilesToBeGeneratedByModelYear =
        pcdfFilesToBeGeneratedByModelYear;
      state.taxImportFile = formattedTaxImportFile;
      state.pcdfFilesByModelYearAndModelCode =
        updatedPcdfFilesByModelYearAndModelCode;
      state.pcdfsByModelYear = formattedPcdfsByModelYear;
      state.toolboxByModelYear = updatedToolboxByModelYear;
      state.pfaByModelYear = updatedPfaByModelYear;
      state.publishOverviewFiles = updatedPublishOverviewFiles;
    },
    setPCDFFiles: (state, action) => {
      const {
        haveAllSourceFiles,
        generateFilesSectionDescription,
        formattedPplFilesByModelYear,
        formattedTaxImportFile,
        updatedPcdfFilesByModelYearAndModelCode,
        formattedPcdfsByModelYear,
        pcdfFilesToBeGeneratedByModelYear,
        updatedToolboxByModelYear,
        updatedPfaByModelYear,
        updatedPublishOverviewFiles,
      } = checkAndGetGenerateAndUpdateStates({
        imdFile: state.imdFile,
        priceMatrixFilesByModelYear: state.priceMatrixFilesByModelYear,
        pplFilesByModelYearAndModelCode: state.pplFilesByModelYearAndModelCode,
        taxImportFile: state.taxImportFile,
        pcdfFilesByModelYearAndModelCode: action.payload,
        toolboxByModelYear: state.toolboxByModelYear,
        pfaByModelYear: state.pfaByModelYear,
        publishOverviewFiles: state.publishOverviewFiles,
      });
      state.haveAllSourceFiles = haveAllSourceFiles;
      state.generateFilesSectionDescription = generateFilesSectionDescription;
      state.pplFilesByModelYear = formattedPplFilesByModelYear;
      state.pcdfFilesToBeGeneratedByModelYear =
        pcdfFilesToBeGeneratedByModelYear;
      state.taxImportFile = formattedTaxImportFile;
      state.pcdfFilesByModelYearAndModelCode =
        updatedPcdfFilesByModelYearAndModelCode;
      state.pcdfsByModelYear = formattedPcdfsByModelYear;
      state.toolboxByModelYear = updatedToolboxByModelYear;
      state.pfaByModelYear = updatedPfaByModelYear;
      state.publishOverviewFiles = updatedPublishOverviewFiles;
    },
    setToolbox: (state, action) => {
      const {
        haveAllSourceFiles,
        generateFilesSectionDescription,
        formattedPplFilesByModelYear,
        formattedTaxImportFile,
        updatedPcdfFilesByModelYearAndModelCode,
        formattedPcdfsByModelYear,
        pcdfFilesToBeGeneratedByModelYear,
        updatedToolboxByModelYear,
        updatedPfaByModelYear,
        updatedPublishOverviewFiles,
      } = checkAndGetGenerateAndUpdateStates({
        imdFile: state.imdFile,
        priceMatrixFilesByModelYear: state.priceMatrixFilesByModelYear,
        pplFilesByModelYearAndModelCode: state.pplFilesByModelYearAndModelCode,
        taxImportFile: state.taxImportFile,
        pcdfFilesByModelYearAndModelCode:
          state.pcdfFilesByModelYearAndModelCode,
        toolboxByModelYear: action.payload,
        pfaByModelYear: state.pfaByModelYear,
        publishOverviewFiles: state.publishOverviewFiles,
      });
      state.haveAllSourceFiles = haveAllSourceFiles;
      state.generateFilesSectionDescription = generateFilesSectionDescription;
      state.pplFilesByModelYear = formattedPplFilesByModelYear;
      state.pcdfFilesToBeGeneratedByModelYear =
        pcdfFilesToBeGeneratedByModelYear;
      state.taxImportFile = formattedTaxImportFile;
      state.pcdfFilesByModelYearAndModelCode =
        updatedPcdfFilesByModelYearAndModelCode;
      state.pcdfsByModelYear = formattedPcdfsByModelYear;
      state.toolboxByModelYear = updatedToolboxByModelYear;
      state.pfaByModelYear = updatedPfaByModelYear;
      state.publishOverviewFiles = updatedPublishOverviewFiles;
    },
    setPfa: (state, action) => {
      const {
        haveAllSourceFiles,
        generateFilesSectionDescription,
        formattedPplFilesByModelYear,
        formattedTaxImportFile,
        updatedPcdfFilesByModelYearAndModelCode,
        formattedPcdfsByModelYear,
        pcdfFilesToBeGeneratedByModelYear,
        updatedToolboxByModelYear,
        updatedPfaByModelYear,
        updatedPublishOverviewFiles,
      } = checkAndGetGenerateAndUpdateStates({
        imdFile: state.imdFile,
        priceMatrixFilesByModelYear: state.priceMatrixFilesByModelYear,
        pplFilesByModelYearAndModelCode: state.pplFilesByModelYearAndModelCode,
        taxImportFile: state.taxImportFile,
        pcdfFilesByModelYearAndModelCode:
          state.pcdfFilesByModelYearAndModelCode,
        toolboxByModelYear: state.toolboxByModelYear,
        pfaByModelYear: action.payload,
        publishOverviewFiles: state.publishOverviewFiles,
      });
      state.haveAllSourceFiles = haveAllSourceFiles;
      state.generateFilesSectionDescription = generateFilesSectionDescription;
      state.pplFilesByModelYear = formattedPplFilesByModelYear;
      state.pcdfFilesToBeGeneratedByModelYear =
        pcdfFilesToBeGeneratedByModelYear;
      state.taxImportFile = formattedTaxImportFile;
      state.pcdfFilesByModelYearAndModelCode =
        updatedPcdfFilesByModelYearAndModelCode;
      state.pcdfsByModelYear = formattedPcdfsByModelYear;
      state.toolboxByModelYear = updatedToolboxByModelYear;
      state.pfaByModelYear = updatedPfaByModelYear;
      state.publishOverviewFiles = updatedPublishOverviewFiles;
    },
    openGetEffectiveDatesModal: (state, action) => {
      const { selectedFilesToGenerateOrUpdate, userFileAction } =
        action.payload;
      state.shouldOpenGetEffectiveDatesModal = true;
      state.userFileAction = userFileAction;
      state.selectedFilesToGenerateOrUpdate = {
        ...state.selectedFilesToGenerateOrUpdate,
        ...selectedFilesToGenerateOrUpdate,
      };
    },
    setPCDFGenerationParams: (state, action) => {
      const { startDate, endDate, dataSetId } = action.payload;
      state.pcdfStartDate = startDate;
      state.pcdfEndDate = endDate;
      state.pcdfDataSetId = dataSetId;
    },
    closeGetEffectiveDatesModal: (state) => {
      state.shouldOpenGetEffectiveDatesModal = false;
      state.userFileAction = '';
      state.selectedFilesToGenerateOrUpdate = {};
    },
    updatePcdfSelection: (state, action) => {
      const { modelYear, modelCode, isSelected } = action.payload;
      const { pcdfsByModelYear } = state;

      state.pcdfsByModelYear[modelYear] = pcdfsByModelYear[modelYear].map(
        (pcdf) => {
          if (pcdf.Metadata.modelcode === modelCode) {
            return { ...pcdf, isSelected };
          }
          return pcdf;
        },
      );
    },
    updateTaxImportSelection: (state, action) => {
      const { isSelected } = action.payload;
      state.taxImportFile = {
        ...state.taxImportFile,
        isSelected,
      };
    },
    generateTaxImportInitiated: (state) => {
      state.isGeneratingTaxImport = true;
    },
    generatedTaxImport: (state) => {
      state.isGeneratingTaxImport = false;
    },
    taxImportGenerationErrored: (state, action) => {
      const { title, errorMsg } = action.payload;
      state.taxImportGenerationError = { title, errorMsg };

      if (state.taxImportFile?.EN && state.taxImportFile?.FR) {
        state.taxImportFile = {
          ...state.taxImportFile,
          isUpdating: false,
        };
      }
    },
    initiatedUpdateTaxImport: (state) => {
      state.taxImportFile = {
        ...state.taxImportFile,
        isUpdating: true,
      };
    },
    updatedTaxImport: (state) => {
      state.taxImportFile = {
        ...state.taxImportFile,
        isUpdating: false,
        shouldUpdate: false,
      };
    },
    intitiatedGeneratePcdf: (state, action) => {
      state.pcdfFileGenerationErrorsByModelYear = {};

      const { modelYear, pplKey } = action.payload;

      if (state.pcdfFilesToBeGeneratedByModelYear[modelYear]) {
        state.pcdfFilesToBeGeneratedByModelYear[modelYear] =
          state.pcdfFilesToBeGeneratedByModelYear[modelYear].map((pplFile) => {
            if (pplFile.Key === pplKey) {
              return {
                ...pplFile,
                isGenerating: true,
              };
            }
            return pplFile;
          });
      }
    },
    generatedPcdf: (state, action) => {
      const { modelYear, pplKey } = action.payload;

      if (state.pcdfFilesToBeGeneratedByModelYear[modelYear]) {
        state.pcdfFilesToBeGeneratedByModelYear[modelYear] =
          state.pcdfFilesToBeGeneratedByModelYear[modelYear].map((pplFile) => {
            if (pplFile.Key === pplKey) {
              return {
                ...pplFile,
                isGenerating: false,
              };
            }
            return pplFile;
          });
      }
    },
    pcdfGenerationErrored: (state, action) => {
      const { modelYear, modelCode, title, errorMsg } = action.payload;
      const { pcdfFileGenerationErrorsByModelYear, pcdfsByModelYear } = state;

      if (!pcdfFileGenerationErrorsByModelYear[modelYear]) {
        pcdfFileGenerationErrorsByModelYear[modelYear] = [];
      }
      pcdfFileGenerationErrorsByModelYear[modelYear].push({
        title,
        errorMsg,
      });

      state.pcdfsByModelYear[modelYear] = pcdfsByModelYear[modelYear].map(
        (pcdf) => {
          if (pcdf.Metadata.modelcode === modelCode) {
            return { ...pcdf, isUpdating: false };
          }
          return pcdf;
        },
      );
    },
    pcdfUpdateInitiated: (state, action) => {
      state.pcdfFileGenerationErrorsByModelYear = {};

      const { modelYear, modelCode } = action.payload;
      const { pcdfsByModelYear } = state;

      state.pcdfsByModelYear[modelYear] = pcdfsByModelYear[modelYear].map(
        (pcdf) => {
          if (pcdf.Metadata.modelcode === modelCode) {
            return { ...pcdf, isUpdating: true };
          }
          return pcdf;
        },
      );
    },
    updatedPcdf: (state, action) => {
      const { modelYear, modelCode } = action.payload;
      const { pcdfsByModelYear } = state;

      state.pcdfsByModelYear[modelYear] = pcdfsByModelYear[modelYear].map(
        (pcdf) => {
          if (pcdf.Metadata.modelcode === modelCode) {
            return { ...pcdf, isUpdating: false, shouldUpdate: false };
          }
          return pcdf;
        },
      );
    },
    publishTaxImportInitiated: (state) => {
      const updatedTaxImport = {
        ...state.taxImportFile,
        status: PUBLISH_STATUS.PROCESSING,
      };
      state.taxImportFile = updatedTaxImport;
      state.publishOverviewFiles.push(updatedTaxImport);
    },
    pcdfPublishInitiated: (state, action) => {
      const pcdfFile = { ...action.payload };
      const modelYear = pcdfFile.Metadata.modelyear;
      const modelCode = pcdfFile.Metadata.modelcode;
      const { pcdfsByModelYear } = state;

      state.pcdfsByModelYear[modelYear] = pcdfsByModelYear[modelYear].map(
        (pcdf) => {
          if (pcdf.Metadata.modelcode === modelCode) {
            const pcdfWithStatus = {
              ...pcdf,
              status: PUBLISH_STATUS.PROCESSING,
            };
            state.publishOverviewFiles.push(pcdfWithStatus);
            return pcdfWithStatus;
          }
          return pcdf;
        },
      );
    },
    generateToolboxInitiated: (state, action) => {
      const modelYear = action.payload;
      state.isGeneratingToolboxByModelYear = {
        ...state.isGeneratingToolboxByModelYear,
        [modelYear]: true,
      };
    },
    generatedToolbox: (state, action) => {
      const modelYear = action.payload;
      state.isGeneratingToolboxByModelYear = {
        ...state.isGeneratingToolboxByModelYear,
        [modelYear]: false,
      };
      if (state.toolboxGenerationErrorsByModelYear[modelYear]) {
        state.toolboxGenerationErrorsByModelYear[modelYear] = {};
      }
    },
    toolboxGenerationErrored: (state, action) => {
      const { title, errorMsg, modelYear } = action.payload;
      if (!state.toolboxGenerationErrorsByModelYear[modelYear]) {
        state.toolboxGenerationErrorsByModelYear[modelYear] = {};
      }
      state.toolboxGenerationErrorsByModelYear[modelYear] = {
        title,
        errorMsg,
      };
      if (state.toolboxByModelYear[modelYear]) {
        state.toolboxByModelYear[modelYear] = {
          ...state.toolboxByModelYear[modelYear],
          isUpdating: false,
        };
      }
    },
    updateToolboxSelection: (state, action) => {
      const { isSelected, modelYear } = action.payload;
      state.toolboxByModelYear[modelYear] = {
        ...state.toolboxByModelYear[modelYear],
        isSelected,
      };
    },
    initiatedUpdateToolbox: (state, action) => {
      const modelYear = action.payload;
      state.toolboxByModelYear[modelYear] = {
        ...state.toolboxByModelYear[modelYear],
        isUpdating: true,
      };
    },
    updatedToolbox: (state, action) => {
      const modelYear = action.payload;
      state.toolboxByModelYear[modelYear] = {
        ...state.toolboxByModelYear[modelYear],
        isUpdating: false,
        shouldUpdate: false,
      };
    },
    generatePfaInitiated: (state, action) => {
      const modelYear = action.payload;
      state.isGeneratingPfaByModelYear = {
        ...state.isGeneratingPfaByModelYear,
        [modelYear]: true,
      };
    },
    generatedPfa: (state, action) => {
      const modelYear = action.payload;
      state.isGeneratingPfaByModelYear = {
        ...state.isGeneratingPfaByModelYear,
        [modelYear]: false,
      };
      if (state.pfaGenerationErrorsByModelYear[modelYear]) {
        state.pfaGenerationErrorsByModelYear[modelYear] = {};
      }
    },
    pfaGenerationErrored: (state, action) => {
      const { title, errorMsg, modelYear } = action.payload;
      if (!state.pfaGenerationErrorsByModelYear[modelYear]) {
        state.pfaGenerationErrorsByModelYear[modelYear] = {};
      }
      state.pfaGenerationErrorsByModelYear[modelYear] = {
        title,
        errorMsg,
      };
      if (state.pfaByModelYear[modelYear]) {
        state.pfaByModelYear[modelYear] = {
          ...state.pfaByModelYear[modelYear],
          isUpdating: false,
        };
      }
    },
    updatePfaSelection: (state, action) => {
      const { isSelected, modelYear } = action.payload;
      state.pfaByModelYear[modelYear] = {
        ...state.pfaByModelYear[modelYear],
        isSelected,
      };
    },
    initiatedUpdatePfa: (state, action) => {
      const modelYear = action.payload;
      state.pfaByModelYear[modelYear] = {
        ...state.pfaByModelYear[modelYear],
        isUpdating: true,
      };
    },
    updatedPfa: (state, action) => {
      const modelYear = action.payload;
      state.pfaByModelYear[modelYear] = {
        ...state.pfaByModelYear[modelYear],
        isUpdating: false,
        shouldUpdate: false,
      };
    },
    updateFilePublishStatus: (state, action) => {
      const { publishLogData, file, isEN } = action.payload;

      if (publishLogData) {
        const { pcdfsByModelYear, taxImportFile } = state;
        const { status, publishedAt, publishedBy, version } = publishLogData;
        const fileType = getFileTypeFromOutputFileS3Key(publishLogData.key);

        if (fileType === OUTPUT_FILES.PCDF) {
          const modelYear = file.Metadata.modelyear;
          const modelCode = file.Metadata.modelcode;

          state.pcdfsByModelYear[modelYear] = pcdfsByModelYear[modelYear].map(
            (pcdf) => {
              if (pcdf.Metadata.modelcode === modelCode) {
                let updatedPcdf = { ...pcdf };
                if (isEN && pcdf.EN.FileVersion === version) {
                  updatedPcdf = {
                    ...pcdf,
                    EN: {
                      ...pcdf.EN,
                      status,
                      publishedAt,
                      publishedBy,
                    },
                  };
                }
                if (!isEN && pcdf.FR.FileVersion === version) {
                  updatedPcdf = {
                    ...pcdf,
                    FR: {
                      ...pcdf.FR,
                      status,
                      publishedAt,
                      publishedBy,
                    },
                  };
                }

                const statuses = [updatedPcdf.EN.status, updatedPcdf.FR.status];
                let parentStatus = '';

                if (statuses.includes(PUBLISH_STATUS.PROCESSING)) {
                  parentStatus = PUBLISH_STATUS.PROCESSING;
                } else if (statuses.includes(PUBLISH_STATUS.REJECTED)) {
                  parentStatus = PUBLISH_STATUS.REJECTED;
                } else if (
                  statuses.includes(PUBLISH_STATUS.PUBLISHED) &&
                  statuses.every((s) => s === PUBLISH_STATUS.PUBLISHED)
                ) {
                  parentStatus = PUBLISH_STATUS.PUBLISHED;
                }

                updatedPcdf.status = parentStatus;
                return updatedPcdf;
              }
              return pcdf;
            },
          );
        }

        if (fileType === OUTPUT_FILES.TAX_IMPORT) {
          const updatedTaxImport = { ...taxImportFile };

          if (isEN && taxImportFile.EN.FileVersion === version) {
            updatedTaxImport.EN = {
              ...updatedTaxImport.EN,
              status,
              publishedAt,
              publishedBy,
            };
          }
          if (!isEN && taxImportFile.FR.FileVersion === version) {
            updatedTaxImport.FR = {
              ...updatedTaxImport.FR,
              status,
              publishedAt,
              publishedBy,
            };
          }

          const statuses = [
            updatedTaxImport.EN.status,
            updatedTaxImport.FR.status,
          ];
          let parentStatus = '';

          if (statuses.includes(PUBLISH_STATUS.PROCESSING)) {
            parentStatus = PUBLISH_STATUS.PROCESSING;
          } else if (statuses.includes(PUBLISH_STATUS.REJECTED)) {
            parentStatus = PUBLISH_STATUS.REJECTED;
          } else if (
            statuses.includes(PUBLISH_STATUS.PUBLISHED) &&
            statuses.every((s) => s === PUBLISH_STATUS.PUBLISHED)
          ) {
            parentStatus = PUBLISH_STATUS.PUBLISHED;
          }

          updatedTaxImport.status = parentStatus;
          state.taxImportFile = updatedTaxImport;
        }

        const updatedPublishOverviewFiles = [];

        state.publishOverviewFiles.forEach((overviewFile) => {
          const tempFile = { ...overviewFile };
          const enVersion = tempFile.EN;
          const frVersion = tempFile.FR;

          if (isEN && file.EN.Key === overviewFile.EN.Key) {
            tempFile.EN.status = status;
          }
          if (!isEN && file.FR.Key === overviewFile.FR.Key) {
            tempFile.FR.status = status;
          }

          const statuses = [enVersion.status, frVersion.status];
          let parentStatus = '';

          if (statuses.includes(PUBLISH_STATUS.PROCESSING)) {
            parentStatus = PUBLISH_STATUS.PROCESSING;
          } else if (statuses.includes(PUBLISH_STATUS.REJECTED)) {
            parentStatus = PUBLISH_STATUS.REJECTED;
          } else if (
            statuses.includes(PUBLISH_STATUS.PUBLISHED) &&
            statuses.every((s) => s === PUBLISH_STATUS.PUBLISHED)
          ) {
            parentStatus = PUBLISH_STATUS.PUBLISHED;
          }

          tempFile.status = parentStatus;
          updatedPublishOverviewFiles.push(tempFile);
        });

        state.publishOverviewFiles = updatedPublishOverviewFiles;
      }
    },
    setPublishOverviewFiles: (state, action) => {
      state.publishOverviewFiles = action.payload;
    },
    openConfirmPublishModal: (state, action) => {
      const { selectedFilesForPublishing, userFileAction } = action.payload;
      state.shouldOpenConfirmPublishModal = true;
      state.userFileAction = userFileAction;
      state.selectedFilesForPublishing = {
        ...state.selectedFilesForPublishing,
        ...selectedFilesForPublishing,
      };
    },
    closeConfirmPublishModal: (state) => {
      state.shouldOpenConfirmPublishModal = false;
      state.userFileAction = '';
      state.selectedFilesForPublishing = {};
    },
  },
});

export default incentivesSlice.reducer;
export const {
  setSelectedModelYear,
  setIsSourceFilesSectionCollapsed,
  toggleCollapseSourceFilesSection,
  setIMDFile,
  setPriceMatrixFiles,
  setPPLFiles,
  // tax import start
  setTaxImportFile,
  updateTaxImportSelection,
  generateTaxImportInitiated,
  taxImportGenerationErrored,
  generatedTaxImport,
  initiatedUpdateTaxImport,
  updatedTaxImport,
  publishTaxImportInitiated,
  // tax import end
  openGetEffectiveDatesModal,
  setPCDFGenerationParams,
  closeGetEffectiveDatesModal,
  // pcdf start
  setPCDFFiles,
  updatePcdfSelection,
  intitiatedGeneratePcdf,
  generatedPcdf,
  pcdfGenerationErrored,
  pcdfUpdateInitiated,
  updatedPcdf,
  pcdfPublishInitiated,
  // pcdf end
  // toolbox start
  setToolbox,
  updateToolboxSelection,
  generateToolboxInitiated,
  generatedToolbox,
  toolboxGenerationErrored,
  initiatedUpdateToolbox,
  updatedToolbox,
  // toolbox end
  // pfa start
  setPfa,
  updatePfaSelection,
  generatePfaInitiated,
  generatedPfa,
  pfaGenerationErrored,
  initiatedUpdatePfa,
  updatedPfa,
  // pfa end
  updateFilePublishStatus,
  setPublishOverviewFiles,
  openConfirmPublishModal,
  closeConfirmPublishModal,
} = incentivesSlice.actions;
